import { useQuery, useMutation } from '@tanstack/react-query';
import { frappePost } from '@/lib/frappe';
import type { PublicStatistics, FeaturedTeacher, PlatformSettings, TeacherListItem, TeacherFilters } from '@/types/api';

// Query keys
export const publicKeys = {
  all: ['public'] as const,
  settings: () => [...publicKeys.all, 'settings'] as const,
  statistics: () => [...publicKeys.all, 'statistics'] as const,
  featuredTeachers: () => [...publicKeys.all, 'featured-teachers'] as const,
  teachers: (filters?: TeacherFilters) => [...publicKeys.all, 'teachers', filters] as const,
  teacher: (name: string) => [...publicKeys.all, 'teacher', name] as const,
  teacherSlots: (name: string, days?: number, duration?: number) =>
    [...publicKeys.all, 'teacher-slots', name, days, duration] as const,
  specialties: () => [...publicKeys.all, 'specialties'] as const,
  languages: () => [...publicKeys.all, 'languages'] as const,
  teacherSpecialties: () => [...publicKeys.all, 'teacher-specialties'] as const,
  teacherLanguages: () => [...publicKeys.all, 'teacher-languages'] as const,
  pageData: (page: string, language: string) =>
    [...publicKeys.all, 'page', page, language] as const,
};

// Get platform settings (public)
export function usePlatformSettings() {
  return useQuery({
    queryKey: publicKeys.settings(),
    queryFn: () =>
      frappePost<PlatformSettings>('hudan_lms.api.public.get_platform_settings', {}, false),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Get public statistics
export function usePublicStatistics() {
  return useQuery({
    queryKey: publicKeys.statistics(),
    queryFn: () => frappePost<PublicStatistics>('hudan_lms.api.public.get_statistics', {}, false),
    staleTime: 5 * 60 * 1000,
  });
}

// Get featured teachers — backend returns {success, teachers}; unwrap.
export function useFeaturedTeachers(limit: number = 6) {
  return useQuery({
    queryKey: publicKeys.featuredTeachers(),
    queryFn: async () => {
      const res = await frappePost<{ success: boolean; teachers?: FeaturedTeacher[] }>(
        'hudan_lms.api.public.get_featured_teachers',
        { limit },
        false,
      );
      return res?.teachers ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Get specialties list
export function useSpecialties() {
  return useQuery({
    queryKey: publicKeys.specialties(),
    queryFn: () => frappePost<string[]>('hudan_lms.api.public.get_specialties', {}, false),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Get languages list
export function useLanguages() {
  return useQuery({
    queryKey: publicKeys.languages(),
    queryFn: () => frappePost<string[]>('hudan_lms.api.public.get_languages', {}, false),
    staleTime: 10 * 60 * 1000,
  });
}

// Get page data
export function usePageData(page: string, language: string = 'en') {
  return useQuery({
    queryKey: publicKeys.pageData(page, language),
    queryFn: () =>
      frappePost<Record<string, string>>(
        'hudan_lms.api.public.get_page_data',
        { page, language },
        false
      ),
    staleTime: 5 * 60 * 1000,
    enabled: !!page,
  });
}

// Submit contact form
export function useContactForm() {
  return useMutation({
    mutationFn: (data: { name: string; email: string; subject: string; message: string }) =>
      frappePost<{ success: boolean }>('hudan_lms.api.public.contact_form', data, false),
  });
}

// ============ Teacher Listing APIs ============

interface TeacherListResponse {
  teachers: TeacherListItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// Get teachers list with filters
export function useTeachers(filters?: {
  specialty?: string;
  language?: string;
  min_price?: number;
  max_price?: number;
  search?: string;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}) {
  return useQuery({
    queryKey: publicKeys.teachers(filters),
    queryFn: () =>
      frappePost<TeacherListResponse>(
        'hudan_lms.hudan_lms.doctype.hudan_teacher_profile.hudan_teacher_profile.get_teachers',
        {
          specialty: filters?.specialty,
          language: filters?.language,
          min_price: filters?.min_price,
          max_price: filters?.max_price,
          search: filters?.search,
          page: filters?.page || 1,
          limit: filters?.limit || 12,
          sort_by: filters?.sort_by || 'average_rating',
          sort_order: filters?.sort_order || 'desc',
        },
        false // Allow guest access
      ),
  });
}

// Get single teacher details
export function useTeacherDetails(name: string) {
  return useQuery({
    queryKey: publicKeys.teacher(name),
    queryFn: () =>
      frappePost<TeacherListItem & { availability: unknown[]; courses: unknown[] }>(
        'hudan_lms.hudan_lms.doctype.hudan_teacher_profile.hudan_teacher_profile.get_teacher',
        { name },
        false
      ),
    enabled: !!name,
  });
}

// Bookable slots for a teacher (availability minus existing commitments).
// Slots arrive as ISO strings with UTC offset; render them in the viewer's
// local timezone with Date/Intl.
export function useTeacherSlots(
  teacher: string,
  options?: { days?: number; duration?: number; enabled?: boolean }
) {
  return useQuery({
    queryKey: publicKeys.teacherSlots(teacher, options?.days, options?.duration),
    enabled: (options?.enabled ?? true) && !!teacher,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const res = await frappePost<{
        success: boolean;
        message?: string;
        duration: number;
        timezone: string;
        slots: string[];
      }>(
        'hudan_lms.api.public.get_teacher_slots',
        {
          teacher,
          days: options?.days ?? 14,
          ...(options?.duration ? { duration: options.duration } : {}),
        },
        false
      );
      if (res?.success === false) {
        throw new Error(res?.message || 'Failed to load availability');
      }
      return res;
    },
  });
}

// Get available teacher specialties
export function useTeacherSpecialties() {
  return useQuery({
    queryKey: publicKeys.teacherSpecialties(),
    queryFn: () =>
      frappePost<string[]>(
        'hudan_lms.hudan_lms.doctype.hudan_teacher_profile.hudan_teacher_profile.get_teacher_specialties',
        {},
        false
      ),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Get available teaching languages
export function useTeacherLanguages() {
  return useQuery({
    queryKey: publicKeys.teacherLanguages(),
    queryFn: () =>
      frappePost<string[]>(
        'hudan_lms.hudan_lms.doctype.hudan_teacher_profile.hudan_teacher_profile.get_teacher_languages',
        {},
        false
      ),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}
